import {
  Box,
  Divider,
  Flex,
  Heading,
  Text,
  Tr,
  Td,
  Table,
  Tbody,
  Tooltip,
} from '@chakra-ui/react';
import * as _ from 'lodash';
import React, { createContext, useContext } from 'react';
import { useNavigate } from 'react-router-dom';

import { typeguards as commonTypeguards } from '@finley-technologies/common';
import { Util } from '@finley-technologies/finley-types';

import {
  getBorrowerFinleyDifference,
  isSanitizedJSON,
  verificationValueTypeToFormatterMap,
} from './verificationTablesUtils';
import { ConcentrationLimitValueTypeGQLType } from '../../../../types/dashboard-core-gqlTypes.generated.d';
import { colors } from 'packages/common-frontend/colors';
import { Icon } from 'packages/common-frontend/components/icons';

export type VerificationPipelineRunId = {
  artifactDocumentId: string;
  creditFacilityId: string;
  reportingDate: Util.FullDateStr;
  fileVersion: number;
};

type Navigate = ReturnType<typeof useNavigate>;

interface VerificationNavigator {
  reset: () => void;
  setPipeline: (
    pipelineRunId: VerificationPipelineRunId,
    options?: {
      /** Update location without pushing an entry to the history stack. */
      updateInPlace?: boolean;
    },
  ) => void;
}

type VerificationOptions = {
  navigator: (navigate: Navigate) => VerificationNavigator;
  overrides: {
    fixedCreditFacilityId?: string;
  };
  displayOptions: {
    showTopLevelHeader: boolean;
  };
};

const VerificationOptionsContext = createContext<VerificationOptions>({
  navigator: (navigate) => ({
    reset: () => {
      navigate('/funding-requests');
    },
    setPipeline: (
      { artifactDocumentId, creditFacilityId, reportingDate, fileVersion },
      options,
    ) => {
      const url = `/funding-requests/verifications/pipeline/${artifactDocumentId}/${creditFacilityId}/${reportingDate}/${fileVersion}`;
      if (options?.updateInPlace ?? false) {
        navigate(url, { replace: true });
      } else {
        navigate(url);
      }
    },
  }),
  overrides: {
    fixedCreditFacilityId: undefined,
  },
  displayOptions: {
    showTopLevelHeader: true,
  },
});

export const useVerificationOptions = () =>
  useContext(VerificationOptionsContext);

export const isVerificationPipelineRunId = (
  test: any,
): test is VerificationPipelineRunId => {
  return (
    test.artifactDocumentId &&
    test.creditFacilityId &&
    commonTypeguards.isFullDateStr(test.reportingDate) &&
    _.isInteger(test.fileVersion)
  );
};

export const tableCellBackgroundColor = colors.millet[50];
export const verificationTablesCellWidth = '19%';

export const verificationCellPadding = '8px';
const verificationSubRowCellPadding = '12px 8px';

type VerificationApproveTableHeaderProps = { title: string };
export const VerificationApproveTableHeader = ({
  title,
}: VerificationApproveTableHeaderProps) => {
  return (
    <Box mb="16px">
      <Heading as="h4" size="md" mb="8px">
        {title}
      </Heading>
      <Divider opacity="100%" />
    </Box>
  );
};

type VerificationAccordionCellProps = { isOpen: boolean };
const VerificationAccordionCell = ({
  isOpen,
}: VerificationAccordionCellProps) => {
  return (
    <Flex
      h="100%"
      flexDirection="column"
      justifyContent="center"
      alignItems="center"
    >
      {isOpen ? (
        <Icon
          icon="circle-chevron-up"
          size="large"
          color={colors.aurora[400]}
        />
      ) : (
        <Icon
          icon="circle-chevron-down"
          size="large"
          color={colors.aurora[400]}
        />
      )}
    </Flex>
  );
};

const UndefinedValueCellContent = () => {
  return (
    <Flex h="100%" flexDirection="column" justifyContent="center">
      -
    </Flex>
  );
};

type NumberValueOrUndefinedCellContentProps = {
  value?: Util.Maybe<Util.SanitizedJSON | Util.Primitive>;
  valueType: ConcentrationLimitValueTypeGQLType;
  heavyText: boolean;
  difference?: Util.Maybe<boolean>;
};
const NumberValueOrUndefinedCellContent = ({
  value,
  valueType,
  difference,
  heavyText,
}: NumberValueOrUndefinedCellContentProps) => {
  if (_.isNil(value) || !_.isNumber(value)) {
    return <UndefinedValueCellContent />;
  }
  let fontColor: string = 'black';
  if (difference) {
    fontColor = colors.error;
  } else if (!heavyText) {
    fontColor = colors.gray[500];
  }

  const fontWeight = heavyText ? 'medium' : 'regular';

  return (
    <Flex h="100%" flexDirection="column" justifyContent="center">
      <Text color={fontColor} fontWeight={fontWeight}>
        {verificationValueTypeToFormatterMap[valueType](value)}
      </Text>
    </Flex>
  );
};

type NumberValueOrUndefinedCellProps = {
  value?: Util.Maybe<Util.SanitizedJSON | Util.Primitive>;
  valueType: ConcentrationLimitValueTypeGQLType;
  heavyText: boolean;
  difference?: Util.Maybe<boolean>;
  tooltipLabel?: string;
  isSubRow?: boolean;
};
const NumberValueOrUndefinedCell = ({
  value,
  valueType,
  difference,
  heavyText,
  tooltipLabel,
  isSubRow,
}: NumberValueOrUndefinedCellProps) => {
  if (tooltipLabel) {
    return (
      <Td
        h="40px"
        w={verificationTablesCellWidth}
        padding={
          isSubRow ? verificationSubRowCellPadding : verificationCellPadding
        }
        backgroundColor={difference ? colors.red[100] : undefined}
      >
        {/* Chakra tooltip location breaks with children that don't forward refs
        Pass shouldWrapChildren prop to fix */}
        <Tooltip
          label={tooltipLabel}
          hasArrow
          placement="left"
          shouldWrapChildren
        >
          <NumberValueOrUndefinedCellContent
            value={value}
            valueType={valueType}
            heavyText={heavyText}
            difference={difference}
          />
        </Tooltip>
      </Td>
    );
  }
  return (
    <Td
      h="40px"
      w={verificationTablesCellWidth}
      padding={verificationCellPadding}
      backgroundColor={difference ? colors.red[100] : undefined}
    >
      <NumberValueOrUndefinedCellContent
        value={value}
        valueType={valueType}
        heavyText={heavyText}
        difference={difference}
      />
    </Td>
  );
};

type VerificationTableNumberRowProps = {
  rowLabel: string;
  valueType: ConcentrationLimitValueTypeGQLType;
  priorValue?: Util.Maybe<number>;
  values?: Util.Maybe<Util.SanitizedJSON | Util.Primitive>;
  isAccordion?: boolean;
  isAccordionOpen?: boolean;
  accordionOnClick?: () => void;
  isSubRow?: boolean; // eg. subRow is an individual eligibility criteria key row
  isFinalSubRow?: boolean; // the last sub row may have different styling
  tooltips?: {
    rowLabel?: string;
    finleyValue?: string;
  };
};
export const VerificationTableNumberRow = ({
  rowLabel,
  priorValue,
  values,
  valueType,
  isAccordion,
  isAccordionOpen,
  accordionOnClick,
  isSubRow,
  isFinalSubRow,
  tooltips,
}: VerificationTableNumberRowProps) => {
  const valuesObj = values && isSanitizedJSON(values) ? values : null;
  const finleyValue = valuesObj ? valuesObj.finleyValue : null;
  const borrowerValue = valuesObj ? valuesObj.borrowerValue : null;

  const allDataAvailable: boolean =
    _.isNumber(borrowerValue) && _.isNumber(finleyValue);

  const formattedDifference = getBorrowerFinleyDifference({
    borrowerValue,
    finleyValue,
    valueType,
  });

  return (
    <Table
      variant="unstyled"
      style={{
        tableLayout: 'fixed',
        cursor: 'default',
        borderCollapse: 'separate',
        borderSpacing: 0,
      }}
      mb={
        (isSubRow && !isFinalSubRow) || (isAccordion && isAccordionOpen)
          ? undefined
          : '16px'
      }
      border={
        isAccordion && !isAccordionOpen
          ? `1px solid ${colors.gray[200]}`
          : undefined
      }
      borderRadius="8px"
    >
      <Tbody>
        <Tr
          backgroundColor={tableCellBackgroundColor}
          onClick={accordionOnClick}
          cursor={isAccordion ? 'pointer' : 'default'}
          _hover={
            isAccordion
              ? {
                  backgroundColor: colors.millet[100],
                }
              : undefined
          }
        >
          <Td
            h="40px"
            w={verificationTablesCellWidth}
            flexDirection="column"
            justifyContent="center"
            borderTopLeftRadius={!isSubRow ? '8px' : undefined}
            borderBottomLeftRadius={
              (!isSubRow && !isAccordionOpen) || isFinalSubRow
                ? '8px'
                : undefined
            }
            fontWeight={isSubRow ? 'regular' : 'medium'}
            color={isSubRow ? colors.gray[500] : undefined}
            padding={
              isSubRow ? verificationSubRowCellPadding : verificationCellPadding
            }
          >
            <Tooltip label={tooltips?.rowLabel} hasArrow placement="left">
              {rowLabel}
            </Tooltip>
          </Td>

          <NumberValueOrUndefinedCell
            value={priorValue}
            valueType={valueType}
            heavyText={false}
            isSubRow={isSubRow}
          />
          <NumberValueOrUndefinedCell
            value={borrowerValue}
            valueType={valueType}
            heavyText={!isSubRow}
            isSubRow={isSubRow}
          />
          <NumberValueOrUndefinedCell
            value={finleyValue}
            valueType={valueType}
            heavyText={!isSubRow}
            tooltipLabel={tooltips?.finleyValue}
            isSubRow={isSubRow}
          />
          <NumberValueOrUndefinedCell
            value={allDataAvailable && formattedDifference}
            valueType={valueType}
            heavyText={!isSubRow}
            difference={allDataAvailable && !!formattedDifference}
            isSubRow={isSubRow}
          />
          <Td
            padding={verificationCellPadding}
            h="40px"
            w="5%"
            borderTopRightRadius={
              !isSubRow || isFinalSubRow ? '8px' : undefined
            }
            borderBottomRightRadius={
              (!isSubRow && !isAccordionOpen) || isFinalSubRow
                ? '8px'
                : undefined
            }
          >
            {isAccordion && (
              <VerificationAccordionCell isOpen={isAccordionOpen || false} />
            )}
          </Td>
        </Tr>
      </Tbody>
    </Table>
  );
};

export const VerificationBorrowerRecentWaiverUpdateIcon = () => {
  return (
    <Icon
      icon="circle"
      type="solid"
      color={colors.error}
      size="8px"
      style={{ marginRight: '8px' }}
    />
  );
};
